import { Injectable } from '@angular/core';
import { HttpService } from 'src/app/core/http/http.service';
import { ExamTypes, ExamsOrdersDialogResponse, ExamsResponse, examTypes } from '../medical-record-page/pages/exams-orders/model/exams.model';
import { PatientService } from 'src/app/shared/services/patient/patient-info.service';
import { SolicitationResponse, SolicitationResponseString } from '../medical-record-page/pages/exams-orders/model/solicitations.model';
import { MedicalRecordState } from 'src/app/features/medical-record/store/medical-record.state';
import { Store } from '@ngrx/store';
import { getAuthStateSelect } from 'src/app/core/store/auth/auth.selector';
import { AuthState } from 'src/app/core/store/auth/auth.state';
import { IUser } from 'src/app/core/models/user.model';
import { getSolicitations } from 'src/app/features/medical-record/store/medical-record.selector';
import { PatientInfoAPI } from '../medical-record-page/pages/patient-informations/model/patient.model';
import { saveSolicitations, setSolicitations, updateSolicitation } from 'src/app/features/medical-record/store/medical-record.action';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExamsOrdersService {

  private cache: Record<ExamTypes, ExamsResponse | null> = {
    imagem: null,
    laboratorial: null
  }

  patientId: string | null = ''
  userLogged: IUser | null = null;
  patientInfo: PatientInfoAPI | null = null;

  constructor(
    private httpService: HttpService,
    private patientInfoService: PatientService,
    private store: Store<MedicalRecordState>,
  ) {
    this.patientInfoService.selectedPatientObs.subscribe(patient => {
      this.patientInfo = patient;
      this.patientId = patient?.patientId!;
    });
    this.store.select(getAuthStateSelect).subscribe((user: AuthState) => {
      this.userLogged = user.user;
    });
  }

  async initialize() {
    examTypes.forEach(examType => {
      this.getExams(examType)
    })
  }

  async getExams(type: ExamTypes, options = { forceFeth: false }): Promise<ExamsResponse> {
    const currentCache = this.cache[type];
    if (currentCache) {
      return currentCache;
    }
    const exams = await this.getExamsDirect(type);
    this.cache[type] = exams;
    return exams;
  }

  // get exams sem usar cache
  private async getExamsDirect(type: ExamTypes) {
    const apiResult = await this.httpService.req<ExamsResponse>('getExamsAndLaboratory', 'patient', {
      apiVersion: 2,
      params: {
        lg: 'pt-BR',
        examType: type,
        coverageId: '55'
      },
      method: 'get'
    });

    if (apiResult.status === 'notok') {
      throw new Error(apiResult.internalStatusCode)
    }
    const { result } = apiResult;
    return (result);
  }

  async getCurrentPatientSolicitations(patientId?: string) {
    if (!patientId) {
      return []
    }
    return this.getSolicitations(patientId)
  }

  async getSolicitations(patientId: string): Promise<SolicitationResponse[]> {
    const apiResult = await this.httpService.req<SolicitationResponseString[]>('getSolicitations', 'patient', {
      apiVersion: 2,
      params: {
        lg: 'pt-BR',
        patientId: patientId
      },
      method: 'get'
    });

    if (apiResult.status === 'notok') {
      throw new Error(apiResult.internalStatusCode)
    }
    const dateTimeNow = new Date();
    const today = new Date(dateTimeNow.getFullYear(), dateTimeNow.getMonth(), dateTimeNow.getDate());
    const { result } = apiResult;
    return result.map(sol => ({
      ...sol,
      allowEdit: new Date(sol.solicitationDate) >= today && sol.status === 'draft',
      solicitationDate: new Date(sol.solicitationDate),
      examType: sol.examType.toLowerCase() as ExamTypes,
      displayResults: sol.examResults.length > 0,
      tags: {
        ...sol.tags,
        accomplishStart: sol.tags.accomplishStart ? new Date(sol.tags.accomplishStart) : null,
        accomplishEnd: sol.tags.accomplishEnd ? new Date(sol.tags.accomplishEnd) : null,
      }
    })).sort((a, b) => b.solicitationDate.valueOf() - a.solicitationDate.valueOf());
  }

  async postSolicitation(obj: {
    examType: ExamTypes,
    examNames: string[],
    laboratoryName: string,
    indication: string,
    suggestWhenTakeExam: ExamsOrdersDialogResponse['suggestWhenTakeExam']
  }): Promise<SolicitationResponse | null> {

    if (this.patientInfo == null || this.userLogged == null) {
      console.error('sem informação de paciente ou usuario no serviço solicitation')
      return null;
    }

    const newItem: SolicitationResponse = {
      examType: obj.examType,
      key: this.patientId!,
      patientId: this.patientId!,
      laboratoryName: obj.laboratoryName,
      practitionerName: this.userLogged.name.toLocaleUpperCase(),
      practitionerId: this.userLogged.practitionerId,
      solicitationDate: new Date(),
      tags: {
        patientName: this.patientInfo.patientName,
        coverageName: this.patientInfo.patientCoverage,
        coveragePlan: this.patientInfo.patientPlan,
        registration: this.patientInfo.patientCoverNumber,
        clinicalIndication: obj.indication,
        CID10: this.patientInfo.patientMedicalRecord.diagnosisCid10,
        exame: obj.examNames,
        accomplishStart: obj.suggestWhenTakeExam.start,
        accomplishEnd: obj.suggestWhenTakeExam.end,
        accomplishMonthYear: obj.suggestWhenTakeExam.monthYear,
        laboratory: obj.laboratoryName,
        nomeMedico: this.userLogged.name.toLocaleUpperCase(),
        CRMMedico: this.userLogged.medicalCouncilNumber
      },
      templateInName: '',
      templateOut: '',
      allowEdit: true,
      displayResults: false,
      clinic: this.userLogged.clinic.clinic,
      templateName: '',
      solicitationId: '',
      examResults: [],
      status: 'draft'
    }

    try {
      const apiResult = await this.httpService.req<any[]>('postSolicitationExams', 'patient', {
        apiVersion: 2,
        params: { patientId: this.patientInfo.patientId ?? '' },
        body: newItem,
        method: 'post'
      });
      if (apiResult.status === 'notok') {
        throw new Error(apiResult.internalStatusCode);
      }
      newItem.solicitationId = (apiResult.result as any).id;
      this.store.dispatch(saveSolicitations({ newItem }));
    } catch (error) {
      console.error('error create exams order', error);
      throw new Error('error on request');
    }
    return newItem as SolicitationResponse;
  }

  async editSolicitation(obj: {
    indexToEdit: number,
    examType: ExamTypes,
    examNames: string[],
    laboratoryName: string,
    indication: string,
    suggestWhenTakeExam: ExamsOrdersDialogResponse['suggestWhenTakeExam'],
    solicitationId: string,
    examResults: SolicitationResponse['examResults'],
    status: SolicitationResponse['status'],
  }): Promise<SolicitationResponse | null> {
    if (this.patientInfo == null || this.userLogged == null) {
      console.error('sem informação de paciente ou usuario no serviço solicitation')
      return null;
    }

    const solicitation: SolicitationResponse = {
      examType: obj.examType,
      key: this.patientId!,
      patientId: this.patientId!,
      laboratoryName: obj.laboratoryName,
      practitionerName: this.userLogged.name.toLocaleUpperCase(),
      practitionerId: this.userLogged.practitionerId,
      solicitationDate: new Date(),
      tags: {
        patientName: this.patientInfo.patientName,
        coverageName: this.patientInfo.patientCoverage,
        coveragePlan: this.patientInfo.patientPlan,
        registration: this.patientInfo.patientCoverNumber,
        clinicalIndication: obj.indication,
        CID10: this.patientInfo.patientMedicalRecord.diagnosisCid10,
        exame: obj.examNames,
        accomplishStart: obj.suggestWhenTakeExam.start,
        accomplishEnd: obj.suggestWhenTakeExam.end,
        accomplishMonthYear: obj.suggestWhenTakeExam.monthYear,
        laboratory: obj.laboratoryName,
        nomeMedico: this.userLogged.name.toLocaleUpperCase(),
        CRMMedico: this.userLogged.medicalCouncilNumber
      },
      templateInName: '',
      templateOut: '',
      allowEdit: true,
      displayResults: false,
      clinic: this.userLogged.clinic.clinic,
      templateName: '',
      solicitationId: obj.solicitationId,
      examResults: obj.examResults,
      status: obj.status,
    }

    try {
      // TODO: verificar pq não funciona criar uma action que da update em 1 item do array apenas, inves de mandar o array inteiro atualizado; nivel de importancia baixissimo
      const currentSolicitations = await firstValueFrom(this.store.select(getSolicitations));
      if (currentSolicitations === null) {
        throw Error('current solicitation null on edit')
      }
      const solicitations = [...currentSolicitations];
      solicitations[obj.indexToEdit] = solicitation;

      const apiResult = await this.httpService.req<any[]>('putSolicitationExams', 'patient', {
        apiVersion: 2,
        params: { patientId: this.patientId ?? '' },
        body: solicitation,
        method: 'put'
      });

      if (apiResult.status === 'notok') {
        throw new Error(apiResult.internalStatusCode)
      }
      this.store.dispatch(setSolicitations({ solicitations }));
    } catch (error) {
      console.error('error update exams order', error);
      throw new Error('error on request');
    }

    return solicitation as SolicitationResponse;
  }

  async removeSolicitation(indexToDelete: number) {
    const currentSolicitations = await firstValueFrom(this.store.select(getSolicitations));
    if (currentSolicitations === null) {
      throw Error('current solicitation null on edit');
    }
    const solicitations = [...currentSolicitations].filter((_, i) => i !== indexToDelete);

    try {
      const apiResult = await this.httpService.req<any[]>('deleteSolicitationExams', 'patient', {
        apiVersion: 2,
        params: { patientId: this.patientId ?? '', solicitationId: currentSolicitations[indexToDelete].solicitationId },
        method: 'delete'
      });

      if (apiResult.status === 'notok') {
        throw new Error(apiResult.internalStatusCode)
      }
      this.store.dispatch(setSolicitations({ solicitations }));
    } catch (error) {
      console.error('error delete exam order', error);
      throw new Error('error on request');
    }
    return 'ok';
  }

  async uploadExamResult(examOrder: SolicitationResponse, obj: {
    fileName: string,
    fileBase64: string,
  }): Promise<SolicitationResponse['examResults']> {

    if (this.patientInfo == null || this.userLogged == null) {
      console.error('sem informação de paciente ou usuario no serviço solicitation');
      throw new Error('no patient info');
    }
    try {
      const apiResult = await this.httpService.req<any[]>('uploadExamResults', 'patient', {
        apiVersion: 2,
        params: { patientId: this.patientInfo.patientId ?? '' },
        body: {
          patientId: this.patientId!,
          solicitationId: examOrder.solicitationId,
          clinicName: this.userLogged.clinic.clinic,
          fileName: obj.fileName,
          fileBase64: obj.fileBase64
        },
        method: 'post'
      });
      if (apiResult.status === 'notok') {
        throw new Error(apiResult.internalStatusCode);
      }
      const currentExamOrder: SolicitationResponse = {
        ...examOrder,
        examResults: [...examOrder.examResults, apiResult.result[0]],
        displayResults: true
      };
      this.store.dispatch(updateSolicitation(currentExamOrder));
      return apiResult.result;
    } catch (error) {
      console.error('error upload exams result', error);
      throw new Error('error on request');
    }
  }

  async getAllExamsResults(patientId: string) {
    try {
      const apiResult = await this.httpService.req<any[]>('getAllExamsResults', 'patient', {
        apiVersion: 2,
        params: {
          patientId: patientId,
          clinicName: this.userLogged?.clinic.clinic ?? ''
        },
        method: 'get'
      });
      if (apiResult.status === 'notok') {
        throw new Error(apiResult.internalStatusCode);
      }
      return apiResult.result as SolicitationResponse['examResults'];
    } catch (error) {
      console.error('error get all exams result', error);
      throw new Error('error on request');
    }
  }

  async getExamResultsByOrder(solicitationId: string) {
    try {
      const apiResult = await this.httpService.req<any[]>('getExamResultsByOrder', 'patient', {
        apiVersion: 2,
        params: {
          solicitationId: solicitationId,
          clinicName: this.userLogged?.clinic.clinic ?? ''
        },
        method: 'get'
      });
      if (apiResult.status === 'notok') {
        throw new Error(apiResult.internalStatusCode);
      }
      return apiResult.result as SolicitationResponse['examResults'];
    } catch (error) {
      console.error('error get all exams result', error);
      throw new Error('error on request');
    }
  }

  async registerMedicalCare(patientId: string, practitionerId: string) {
    try {
      const apiResult = await this.httpService.req<any[]>('registerSolicitationExams', 'patient', {
        apiVersion: 2,
        params: { patientId, practitionerId },
        body: { patientId, practitionerId },
        method: 'post'
      });

      if (apiResult.status === 'ok') {
        return 'ok';
      } else {
        throw new Error(apiResult.internalStatusCode);
      }
    } catch (error) {
      console.error('error registier medical care', error);
      throw new Error('error on request');
    }
  }

  async cancelMedicalCare(patientId: string, practitionerId: string) {
    try {
      const apiResult = await this.httpService.req<any[]>('cancelSolicitationExams', 'patient', {
        apiVersion: 2,
        params: { patientId, practitionerId },
        body: { patientId, practitionerId },
        method: 'delete'
      });

      if (apiResult.status === 'ok') {
        return 'ok';
      } else {
        throw new Error(apiResult.internalStatusCode);
      }
    } catch (error) {
      console.error('error cancel medical care', error);
      throw new Error('error on request');
    }
  }

}
